// Single source of truth for appointment status, transitions, time windows, and conflict checks.
// Keep this UI-only: backend payloads still use the canonical names (Scheduled, etc.).

export const STATUS = Object.freeze({
  Scheduled:  'Scheduled',
  Confirmed:  'Confirmed',
  Engaged:    'InProgress', // backend uses InProgress; UI shows "Engaged"
  CheckedOut: 'Completed',  // backend uses Completed; UI shows "Checked Out"
  NoShow:     'NoShow',
  Cancelled:  'Cancelled',
});

// Display metadata: label shown to the user + color for badges/cards.
export const STATUS_META = {
  Scheduled:  { label: 'Scheduled',   color: 'var(--blue-500)',   bg: 'var(--blue-50)',    text: 'var(--blue-800)'    },
  Confirmed:  { label: 'Confirmed',   color: 'var(--blue-600)',   bg: 'var(--blue-100)',   text: 'var(--blue-800)'    },
  InProgress: { label: 'Engaged',     color: 'var(--amber-500)',  bg: 'var(--amber-100)',  text: '#92400e'            },
  Completed:  { label: 'Checked Out', color: 'var(--green-600)',  bg: 'var(--green-100)',  text: 'var(--green-800)'   },
  NoShow:     { label: 'No Show',     color: 'var(--red-500)',    bg: 'var(--red-50)',     text: 'var(--red-700)'     },
  Cancelled:  { label: 'Cancelled',   color: 'var(--gray-400)',   bg: 'var(--gray-100)',   text: 'var(--gray-600)'    },
};

export function statusLabel(s)  { return STATUS_META[s]?.label ?? s ?? '—'; }
export function statusColor(s)  { return STATUS_META[s]?.color ?? 'var(--gray-400)'; }
export function statusBg(s)     { return STATUS_META[s]?.bg ?? 'var(--gray-100)'; }
export function statusText(s)   { return STATUS_META[s]?.text ?? 'var(--text-secondary)'; }

// Engage window: from `engageLeadMinutes` before the slot starts to
// `engageTrailMinutes` past its scheduled end.
export const ENGAGE_LEAD_MIN  = 10;
export const ENGAGE_TRAIL_MIN = 15;

/** Returns { ok, reason } describing whether engage is allowed *now* for the given appointment. */
export function canEngage(appt, now = new Date()) {
  if (!appt) return { ok: false, reason: 'No appointment selected' };
  if (appt.status === 'InProgress') return { ok: false, reason: 'Already engaged' };
  if (appt.status === 'Completed')  return { ok: false, reason: 'Already checked out' };
  if (appt.status === 'NoShow')     return { ok: false, reason: 'Marked as no-show' };
  if (appt.status === 'Cancelled')  return { ok: false, reason: 'Cancelled' };

  const start = new Date(appt.scheduledAt).getTime();
  const end   = start + ((appt.durationMinutes ?? 30) * 60_000);
  const lead  = start - ENGAGE_LEAD_MIN  * 60_000;
  const trail = end   + ENGAGE_TRAIL_MIN * 60_000;
  const t = now.getTime();
  if (t < lead)  return { ok: false, reason: `Too early — engage opens at ${new Date(lead).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}` };
  if (t > trail) return { ok: false, reason: 'Window closed — engage was allowed up to 15 min after the scheduled end' };
  return { ok: true };
}

/** Checkout is only allowed once the appointment is engaged. */
export function canCheckout(appt) {
  if (!appt) return { ok: false, reason: 'No appointment selected' };
  if (appt.status !== 'InProgress') return { ok: false, reason: 'Engage the patient before checking out' };
  return { ok: true };
}

/** No-show / cancellation gating. */
export function canMarkNoShow(appt) {
  if (!appt) return { ok: false, reason: 'No appointment selected' };
  if (appt.status === 'Completed') return { ok: false, reason: 'Already checked out' };
  if (appt.status === 'NoShow')    return { ok: false, reason: 'Already no-show' };
  if (appt.status === 'Cancelled') return { ok: false, reason: 'Cancelled' };
  return { ok: true };
}
export function canCancel(appt) {
  if (!appt) return { ok: false, reason: 'No appointment selected' };
  if (appt.status === 'Completed') return { ok: false, reason: 'Already checked out' };
  if (appt.status === 'Cancelled') return { ok: false, reason: 'Already cancelled' };
  return { ok: true };
}

/**
 * Returns whether the appointment can be extended by `addMinutes` without colliding
 * with any other appointment that shares the same assignedUserId.
 *
 * @param appt           the appointment being extended
 * @param addMinutes     15 | 30 | 60
 * @param allAppointments the calendar list currently in scope
 */
export function canExtend(appt, addMinutes, allAppointments = []) {
  if (!appt) return { ok: false, reason: 'No appointment selected' };
  if (appt.status === 'Completed') return { ok: false, reason: 'Already checked out — cannot extend' };
  if (appt.status === 'Cancelled' || appt.status === 'NoShow') {
    return { ok: false, reason: 'Cannot extend a cancelled or no-show appointment' };
  }

  const start = new Date(appt.scheduledAt).getTime();
  const oldEnd = start + ((appt.durationMinutes ?? 30) * 60_000);
  const newEnd = oldEnd + addMinutes * 60_000;

  // Find any appointment with the SAME doctor that starts inside [oldEnd, newEnd)
  const blocker = allAppointments.find((other) => {
    if (other.id === appt.id) return false;
    if (other.assignedUserId !== appt.assignedUserId) return false;
    if (other.status === 'Cancelled' || other.status === 'NoShow') return false;
    const oStart = new Date(other.scheduledAt).getTime();
    return oStart >= oldEnd && oStart < newEnd;
  });

  if (blocker) {
    const t = new Date(blocker.scheduledAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });
    return { ok: false, reason: `Conflicts with next appointment at ${t}` };
  }
  return { ok: true, newDurationMinutes: (appt.durationMinutes ?? 30) + addMinutes };
}

/**
 * Detects conflicts when CREATING a new appointment.
 * Returns the conflicting appointment (or null).
 *  - same doctor
 *  - active status (not cancelled / no-show)
 *  - time ranges overlap
 */
export function findConflict({ assignedUserId, scheduledAt, durationMinutes }, allAppointments = []) {
  if (!assignedUserId || !scheduledAt) return null;
  const start = new Date(scheduledAt).getTime();
  const end   = start + ((durationMinutes ?? 30) * 60_000);
  return allAppointments.find((o) => {
    if (o.assignedUserId !== assignedUserId) return false;
    if (o.status === 'Cancelled' || o.status === 'NoShow') return false;
    const oStart = new Date(o.scheduledAt).getTime();
    const oEnd   = oStart + ((o.durationMinutes ?? 30) * 60_000);
    return start < oEnd && oStart < end;
  }) ?? null;
}

/**
 * The single label for "what this appointment is" across the whole CRM.
 *
 * There used to be two competing notions on screen. `Appointment.type` is the legacy
 * Consultation / Session / FollowUp enum, which the booking form hardcodes to `Consultation`
 * and the backend force-sets to `Consultation` for a patient's first visit — so it rendered
 * the identical word on essentially every row and told the reader nothing. The real answer is
 * the billed procedure the visit was booked against.
 *
 * `completedTreatmentName` is the server-resolved chain (explicit procedure → the patient's
 * initial "Doctor Consultancy" → the drawn-from session/package → "Session"). It is null on a
 * visit that has not completed yet, and it is deliberately omitted from the calendar projection
 * for speed — in both those cases `treatmentTypeName`, a plain column, is what to show.
 *
 * Use this everywhere an appointment's nature is displayed, so no two screens can disagree.
 *
 * @returns {string|null} the label, or null when the visit has no procedure yet.
 */
export function procedureLabel(appointment) {
  if (!appointment) return null;
  return appointment.completedTreatmentName || appointment.treatmentTypeName || null;
}
